/**
 * SubmissionSuccess Component
 *
 * Success state shown after a deck is successfully submitted.
 * Features:
 * - Animated checkmark with spring entrance
 * - Confirmation card with "what happens next" steps
 * - Email notice
 * - CTA: "Create Your Inverio Profile" (growth loop) + "Submit Another"
 */

import { View, Text } from 'react-native';
import { Check, Clock, Eye, Mail } from 'lucide-react-native';
import { useRouter } from 'expo-router';
import Animated, {
  FadeInDown,
  useSharedValue,
  useAnimatedStyle,
  withSpring,
} from 'react-native-reanimated';
import { useEffect } from 'react';
import AnimatedPressable from '@/components/AnimatedPressable';
import { usePublicThemeColors } from '@/lib/useThemeColors';

// ============================================================================
// Types
// ============================================================================

interface SubmissionSuccessProps {
  companyName: string;
  investorName: string;
  contactEmail: string;
  onSubmitAnother?: () => void;
}

// ============================================================================
// Animated Checkmark
// ============================================================================

function AnimatedCheckmark() {
  const colors = usePublicThemeColors();
  const scale = useSharedValue(0);

  useEffect(() => {
    scale.value = withSpring(1, { damping: 12, stiffness: 100 });
  }, [scale]);

  const circleStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  return (
    <Animated.View
      style={[
        circleStyle,
        {
          width: 80,
          height: 80,
          borderRadius: 40,
          backgroundColor: colors.primary[600],
          alignItems: 'center',
          justifyContent: 'center',
        },
      ]}
    >
      <Animated.View entering={FadeInDown.delay(300).duration(400)}>
        <Check size={40} color="#FFFFFF" strokeWidth={3} />
      </Animated.View>
    </Animated.View>
  );
}

// ============================================================================
// Step Item
// ============================================================================

function StepItem({
  number,
  icon,
  text,
}: {
  number: string;
  icon: React.ReactNode;
  text: string;
}) {
  const colors = usePublicThemeColors();
  return (
    <View className="flex-row items-start gap-3 py-2">
      <View
        className="w-8 h-8 rounded-full items-center justify-center flex-shrink-0"
        style={{ backgroundColor: colors.primary[50] }}
      >
        {icon}
      </View>
      <View className="flex-1 pt-1">
        <Text
          className="text-sm leading-relaxed"
          style={{ color: colors.text.secondary }}
        >
          <Text className="font-semibold" style={{ color: colors.text.primary }}>
            {number}.{' '}
          </Text>
          {text}
        </Text>
      </View>
    </View>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function SubmissionSuccess({
  companyName,
  investorName,
  contactEmail,
  onSubmitAnother,
}: SubmissionSuccessProps) {
  const colors = usePublicThemeColors();
  const router = useRouter();

  return (
    <View className="items-center">
      {/* Success Icon */}
      <View className="mb-5">
        <AnimatedCheckmark />
      </View>

      {/* Heading */}
      <Animated.View entering={FadeInDown.delay(400).duration(500)} style={{ marginBottom: 24 }}>
        <Text
          className="text-2xl font-bold text-center"
          style={{ color: colors.primary[600] }}
        >
          Deck Submitted!
        </Text>
      </Animated.View>

      {/* Confirmation Card */}
      <Animated.View
        entering={FadeInDown.delay(500).duration(500)}
        style={{
          width: '100%',
          marginBottom: 16,
          backgroundColor: colors.surface.primary,
          borderWidth: 1,
          borderColor: colors.border.light,
          borderRadius: 16,
          padding: 20,
        }}
      >
        <Text
          className="text-sm leading-relaxed mb-4"
          style={{ color: colors.text.secondary }}
        >
          Your pitch deck for{' '}
          <Text className="font-semibold" style={{ color: colors.text.primary }}>
            {companyName}
          </Text>{' '}
          has been submitted to{' '}
          <Text className="font-semibold" style={{ color: colors.text.primary }}>
            {investorName}
          </Text>
          .
        </Text>

        {/* Divider */}
        <View
          className="mb-3"
          style={{ height: 1, backgroundColor: colors.border.light }}
        />

        <Text
          className="text-sm font-semibold mb-2"
          style={{ color: colors.text.primary }}
        >
          What happens next:
        </Text>

        <StepItem
          number="1"
          icon={<Clock size={16} color={colors.primary[600]} />}
          text="AI analyzes your deck and generates a score"
        />
        <StepItem
          number="2"
          icon={<Eye size={16} color={colors.primary[600]} />}
          text={`${investorName} reviews your submission`}
        />
        <StepItem
          number="3"
          icon={<Mail size={16} color={colors.primary[600]} />}
          text={`Check ${contactEmail} for your score report`}
        />
      </Animated.View>

      {/* Email Notice */}
      <Animated.View
        entering={FadeInDown.delay(600).duration(500)}
        style={{ marginBottom: 24, width: '100%' }}
      >
        <Text
          className="text-xs text-center leading-relaxed"
          style={{ color: colors.text.tertiary }}
        >
          We've sent a confirmation to{' '}
          <Text className="font-semibold" style={{ color: colors.text.secondary }}>
            {contactEmail}
          </Text>
          . You'll receive your AI score breakdown shortly.
        </Text>
      </Animated.View>

      {/* CTA Buttons */}
      <Animated.View
        entering={FadeInDown.delay(700).duration(500)}
        style={{ width: '100%', gap: 12 }}
      >
        {/* Primary CTA - Growth Loop */}
        <AnimatedPressable
          onPress={() => router.push('/login?source=submission')}
          className="w-full py-3.5 rounded-xl items-center justify-center"
          style={{ backgroundColor: colors.primary[600] }}
        >
          <Text className="text-base font-semibold" style={{ color: '#FFFFFF' }}>
            Create Your Inverio Profile
          </Text>
        </AnimatedPressable>

        {/* Secondary CTA - Submit Another */}
        {onSubmitAnother && (
          <AnimatedPressable
            onPress={onSubmitAnother}
            className="w-full py-3.5 rounded-xl items-center justify-center"
            style={{
              borderWidth: 1.5,
              borderColor: colors.border.medium,
              backgroundColor: 'transparent',
            }}
          >
            <Text
              className="text-base font-semibold"
              style={{ color: colors.text.primary }}
            >
              Submit Another Deck
            </Text>
          </AnimatedPressable>
        )}
      </Animated.View>
    </View>
  );
}
